import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  detectPlatform,
  generateBurokPayNativeDeeplink,
  isMobile,
  storeHrefForPlatform,
} from '../src/utils/burokLinkDeeplink';

const BUROK_LOGO = '/assets/images/BUROKLOGOTWITTER.png';
const BUROK_BACKGROUND = '/assets/images/Burok gradient.png';

/**
 * Fallback install app pour `burok.app/pay`.
 * Affiche destinataire / montant si présents, tente d’ouvrir l’app sur mobile.
 * Le checkout / QR paiement reste sur PayLink Ramp (pas ici).
 */
export default function PayFallbackPage() {
  const [searchParams] = useSearchParams();
  const [platform] = useState(() => detectPlatform());

  const username = searchParams.get('username');
  const amount = searchParams.get('amount');
  const currency = searchParams.get('currency') || 'CFA';

  const queryString = searchParams.toString();
  const nativeUrl = useMemo(
    () => generateBurokPayNativeDeeplink(queryString),
    [queryString]
  );

  useEffect(() => {
    if (!isMobile()) return;
    window.location.href = nativeUrl;
  }, [nativeUrl]);

  const storeHref = storeHrefForPlatform(platform);
  const mobile = isMobile();

  const amountLabel =
    amount && !Number.isNaN(Number(amount))
      ? `${Number(amount).toLocaleString('fr-FR')} ${currency}`
      : null;

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-6 text-white bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${BUROK_BACKGROUND}")` }}
    >
      <img
        src={BUROK_LOGO}
        alt="Burok"
        className="w-20 h-20 mb-6 rounded-2xl shadow-lg object-contain"
      />

      <h1 className="text-2xl font-semibold text-center mb-2 drop-shadow-sm">Payer avec Burok</h1>
      <p className="text-center text-white/90 text-[15px] max-w-md mb-6 leading-relaxed drop-shadow-md">
        Pour continuer, ouvrez l’application Burok. Si elle n’est pas installée, téléchargez-la
        ci‑dessous.
      </p>

      {(username || amountLabel) && (
        <div className="w-full max-w-sm p-5 mb-8 rounded-2xl border border-white/25 bg-white/95 text-gray-900 shadow-xl text-center space-y-2">
          {username && (
            <p className="text-sm text-gray-600">
              Destinataire{' '}
              <span className="font-semibold text-gray-900">@{username.replace(/^@/, '')}</span>
            </p>
          )}
          {amountLabel && (
            <p className="text-lg font-semibold text-gray-900">{amountLabel}</p>
          )}
        </div>
      )}

      {mobile && (
        <div className="flex flex-col items-center mb-6">
          <Loader2 className="w-8 h-8 text-cyan-300 animate-spin mb-3" />
          <p className="text-sm text-white/90 mb-4">Ouverture de Burok…</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
        <a
          href={nativeUrl}
          className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl bg-white text-neutral-900 font-medium hover:bg-neutral-100 transition-colors shadow-lg"
        >
          Ouvrir dans l’app
        </a>
        <a
          href={storeHref}
          className="flex-1 inline-flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-neutral-900 transition-colors shadow-lg"
        >
          <img
            src={BUROK_LOGO}
            alt=""
            className="w-8 h-8 rounded-lg object-contain shrink-0"
            aria-hidden
          />
          Installer Burok
        </a>
      </div>
    </div>
  );
}
